/**
 * The failures the header still shows: failed runs that nothing later has answered.
 *
 * Read only from durable task/run relationships, never from titles or wording:
 *
 * - A failed run is superseded once a later run of the same project and task
 *   succeeded (the same rule the Recent runs list uses), or once that task's own
 *   row says it completed.
 * - A failed run whose task was discarded is history, not a current failure.
 * - A failed run whose own pull request, or a later run's of the same task, is
 *   durably recorded as merged is settled: the work reached the base branch.
 *   The failed run_summaries row itself is never changed.
 * - Several failed attempts at one task are one current failure: the newest.
 *
 * The header count and the list it opens are both this one array, so they
 * cannot disagree. Pure: callers pass the clock in.
 */
import { presentRecentRuns, type SupersessionRun } from './recent-run-supersession';
import { startOfDay } from './stats';

/** A task row's status, keyed by its project and id (mission_tasks or task_actions). */
export interface TaskStatusRow {
  readonly project_id: string;
  readonly task_id: string;
  readonly status: string;
}

/** Task statuses that settle an earlier failed run of the task. */
const SETTLED_TASK_STATUSES: ReadonlySet<string> = new Set(['completed', 'discarded']);

function key(projectId: string, taskId: string): string | null {
  const project = projectId.trim();
  const task = taskId.trim();
  return project === '' || task === '' ? null : `${project}\n${task}`;
}

/**
 * Assignments whose pull request is durably recorded as merged: GitHub reported the merge when
 * the run recorded its PR (completion_pull_requests.merged_at), the completion loop merged the
 * exact head (merge / merged), or it observed the PR merged afterwards (integration /
 * merged_externally). Nothing else counts; an "advance" decision can mean there was no PR.
 */
export function mergedAssignmentsFrom(
  mergedPullRequests: readonly { readonly assignment_id: string }[],
  completionActions: readonly { readonly assignment_id: string; readonly stage: string; readonly outcome: string }[],
): string[] {
  const out = new Set(mergedPullRequests.map((row) => row.assignment_id));
  for (const row of completionActions) {
    if ((row.stage === 'merge' && row.outcome === 'merged')
      || (row.stage === 'integration' && row.outcome === 'merged_externally')) out.add(row.assignment_id);
  }
  return [...out];
}

function reportedMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Failed runs reported since midnight that are still current, newest first,
 * at most one per task. A run with no task id cannot be matched to anything
 * later, so it stays current.
 */
export function currentFailures<T extends SupersessionRun>(input: {
  readonly runs: readonly T[];
  readonly tasks: readonly TaskStatusRow[];
  /** Assignments whose pull request durable evidence shows merged (mergedAssignmentsFrom). */
  readonly mergedAssignments?: readonly string[];
  readonly nowMs: number;
}): T[] {
  const midnight = startOfDay(input.nowMs);
  const views = presentRecentRuns(input.runs);
  const settled = new Set<string>();
  for (const task of input.tasks) {
    if (!SETTLED_TASK_STATUSES.has(task.status)) continue;
    const id = key(task.project_id, task.task_id);
    if (id !== null) settled.add(id);
  }
  // A run whose own pull request was merged settles that run and every earlier failure of its task.
  const merged = new Set(input.mergedAssignments ?? []);
  const mergedThrough = new Map<string, number>();
  for (const run of input.runs) {
    if (!merged.has(run.assignment_id)) continue;
    const id = key(run.project_id, run.task_id);
    const at = reportedMs(run.reported_at);
    if (id !== null && at > (mergedThrough.get(id) ?? Number.NEGATIVE_INFINITY)) mergedThrough.set(id, at);
  }

  const newest = [...input.runs].sort((a, b) => reportedMs(b.reported_at) - reportedMs(a.reported_at));
  const seenTasks = new Set<string>();
  const out: T[] = [];
  for (const run of newest) {
    if (run.success) continue;
    if (reportedMs(run.reported_at) < midnight) continue;
    if (views.get(run.assignment_id)?.superseded === true) continue;
    if (merged.has(run.assignment_id)) continue;
    const id = key(run.project_id, run.task_id);
    if (id !== null) {
      if (reportedMs(run.reported_at) <= (mergedThrough.get(id) ?? Number.NEGATIVE_INFINITY)) continue;
      if (settled.has(id) || seenTasks.has(id)) continue;
      seenTasks.add(id);
    }
    out.push(run);
  }
  return out;
}
